import type mapboxgl from "mapbox-gl";
import { useCallback, useEffect, type FC } from "react";
import {
  getFirstCustomLayerId,
  setBaseStylePreservingLayers,
} from "../../../utils/mapStyleState";
import { ROAD_LABEL_LAYER_IDS } from "../MapControlsPanel.types";

interface RoadNamesToolProps {
  mapRef: mapboxgl.Map | null;
  isEnabled: boolean;
}

/** Satellite imagery with street labels — used when roads are enabled on a pure satellite style. */
const SATELLITE_STREETS_STYLE = "mapbox://styles/mapbox/satellite-streets-v12";

const RoadNamesTool: FC<RoadNamesToolProps> = ({ mapRef, isEnabled }) => {
  const updateRoadLabelsVisibility = useCallback(
    (map: mapboxgl.Map, enabled: boolean): void => {
      const presentLayerIds = ROAD_LABEL_LAYER_IDS.filter((layerId) =>
        map.getLayer(layerId)
      );

      if (!presentLayerIds.length && enabled) {
        // Pure satellite styles ship no road labels — swap to satellite-streets
        // so roads become available, keeping the trial layers intact.
        const style = map.getStyle();
        const styleName = (style?.name ?? style?.sprite ?? "").toLowerCase();

        if (
          styleName.includes("satellite") &&
          !styleName.includes("satellite-streets")
        ) {
          setBaseStylePreservingLayers(map, SATELLITE_STREETS_STYLE);
        }
        return;
      }

      // AC: road names render above the base map but below the trial geometry.
      const firstCustomLayerId = getFirstCustomLayerId(map);

      presentLayerIds.forEach((layerId) => {
        map.setLayoutProperty(
          layerId,
          "visibility",
          enabled ? "visible" : "none"
        );

        if (enabled && firstCustomLayerId && firstCustomLayerId !== layerId) {
          try {
            map.moveLayer(layerId, firstCustomLayerId);
          } catch {
            // Layer ordering is best-effort; never break the toggle over it.
          }
        }
      });
    },
    []
  );

  useEffect(() => {
    if (!mapRef) {
      return;
    }

    if (mapRef.isStyleLoaded()) {
      updateRoadLabelsVisibility(mapRef, isEnabled);
    }

    const handleStyleData = (): void => {
      if (mapRef.isStyleLoaded()) {
        updateRoadLabelsVisibility(mapRef, isEnabled);
      }
    };

    mapRef.on("styledata", handleStyleData);

    return () => {
      try {
        mapRef.off("styledata", handleStyleData);
      } catch {
        // Expected while the map is unmounting.
      }
    };
  }, [mapRef, isEnabled, updateRoadLabelsVisibility]);

  return null;
};

export default RoadNamesTool;
